import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import PropTypes from "prop-types";
import withStyles from "react-jss";
import { Link, Route, Switch, withRouter } from "react-router-dom";
import Select, { components } from "react-select";
import Card from "react-toolbox/lib/card";
import COLOURS from "util/colours";
import { H2, H3 } from "visual-components/util/texts";
import Pagination from "./pagination";

const styles = {
  container: {
    padding: 40,
  },
  heading: {
    marginBottom: 24,
  },
  filterSection: {
    marginBottom: 24,
    padding: 16,
    border: `1px solid ${COLOURS.silver}`,
    borderRadius: 8,
    backgroundColor: "#fff",
  },
  filterRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 24,
  },
  filterItem: {
    flex: "1 1 250px",
    minWidth: 220,
    display: "flex",
    flexDirection: "column",
    marginLeft: 12,
  },
  filterLabel: {
    marginBottom: 6,
    fontWeight: 500,
    fontSize: 14,
    color: "#333",
  },
  filterInput: {
    width: "100%",
    padding: "8px 12px",
    fontSize: "14px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    outline: "none",
    transition: "border-color 0.2s",

    "&:focus": {
      borderColor: "#007bff",
      boxShadow: "0 0 3px rgba(0,123,255,0.5)",
    },
  },
  filterButton: {
    marginLeft: 16,
    backgroundColor: "transparent",
    border: "1px solid #007FFF",
    color: "#007FFF",
    padding: "6px 12px",
    borderRadius: 6,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#007FFF",
      color: "#fff",
    },
  },
  loader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 16,
    "& .spinner": {
      width: 24,
      height: 24,
      border: "4px solid #007FFF",
      borderTop: "4px solid transparent",
      borderRadius: "50%",
      animation: "$spin 1s linear infinite",
    },
  },
  tableWrapper: {
    marginTop: 16,
    borderRadius: 8,
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: 14,
  },
  tableHeader: {
    backgroundColor: COLOURS.silver,
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  th: {
    padding: "12px 16px",
    textAlign: "left",
    borderBottom: "2px solid #ddd",
    fontWeight: 600,
    backgroundColor: "#f5f7fa",
  },
  td: {
    padding: "12px 16px",
    borderBottom: "1px solid #eee",
  },
  tableRow: {
    "&:nth-child(even)": {
      backgroundColor: "#fafafa",
    },
    "&:hover": {
      backgroundColor: "#eaf4ff",
      transition: "0.2s",
    },
  },
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
};

function CSAdminCMS(props) {
  const { classes } = props;
  const [contents, setContents] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [error, setError] = useState("");

  const fetchContents = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("limit", limit.toString());
      if (keyword) params.append("keyword", keyword);

      const res = await fetch(`/cs-contents?${params.toString()}`);
      const data = await res.json();

      setContents(data.contents || []);
      setTotal(data.total || 0);
      setPage(data.page || 1);
    } catch (err) {
      setError("Failed to fetch contents.");
      console.error("Error fetching CMS content:", err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchContents(1);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [keyword]);

  useEffect(() => {
    fetchContents();
  }, [page]);

  const getStatusColor = (status) => {
    switch (status) {
      case 1:
        return "#d4edda"; // light green
      case 3:
        return "#f8d7da"; // light red
      case 2:
        return "#fff3cd"; // light yellow
      default:
        return "#ffffff"; // default white
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.heading}>
        <H2 azure>CS Admin - Content Management System</H2>
      </div>

      {/* Filter Section */}
      <div className={classes.filterSection}>
        <H3>Filter Content</H3>

        <div className={classes.filterRow}>
          <div className={classes.filterItem}>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Search content by title , description..."
              className={classes.filterInput}
            />
          </div>
        </div>
        <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12 }}>
          <Link to="/cs-admin/content">
            <button
              className={classes.filterButton}
              disabled={loading}
              style={{
                opacity: loading ? 0.6 : 1,
                cursor: loading ? "not-allowed" : "pointer",
              }}
            >
              {"Create Content"}
            </button>
          </Link>
        </div>
      </div>
      {loading && (
        <div style={{ textAlign: "center", marginTop: "40px" }}>
          <div className="spinner"></div>
          <p>Loading contents...</p>
        </div>
      )}
      {error && (
        <div style={{ textAlign: "center", marginTop: "40px" }}>
          <div className="spinner"></div>
          <p style={{ color: "red" }}>{error}</p>
        </div>
      )}
      {contents.length > 0 ? (
        <div style={{ marginTop: 32 }}>
          <H3>Contents:</H3>
          <div className={classes.filterRow} style={{ marginTop: 15 }}></div>
          <table
            className={classes.table}
            style={{ marginTop: 32, borderSpacing: "8px", borderCollapse: "separate" }}
          >
            <thead className={classes.tableHeader}>
              <tr>
                <th className={classes.th}>Title</th>
                <th className={classes.th}>Status</th>
                <th className={classes.th}>Last updated</th>
                <th className={classes.th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {contents.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: "16px", textAlign: "center", color: "#999" }}>
                    No Campaign found.
                  </td>
                </tr>
              ) : (
                contents.map((content) => {
                  let statusColor = "black"; // default
                  let StatusName = "Active";
                  if (content.status === 0) {
                    statusColor = "grey";
                    StatusName = "Draft";
                  }
                  if (content.status === 1) {
                    statusColor = "green";
                    StatusName = "Active";
                  } else if (content.status === 2) {
                    StatusName = "Inactive";
                    statusColor = "orange";
                  } else if (content.status === 3) {
                    statusColor = "red"; // or yellow
                    StatusName = "Archived";
                  }

                  return (
                    <tr
                      key={content.id}
                      className={classes.tableRow}
                      style={{
                        backgroundColor: getStatusColor(content.status),
                        borderRadius: "8px",
                      }}
                    >
                      <td className={classes.td}>{content.title}</td>

                      <td
                        className={classes.td}
                        style={{
                          color: statusColor,
                          fontWeight: "bold",
                          border: `1px solid ${statusColor}`,
                          borderRadius: "8px",
                          padding: "6px 12px",
                          textAlign: "center",
                          backgroundColor: `${statusColor}20`,
                        }}
                      >
                        {StatusName}
                      </td>
                      <td className={classes.td}>
                        {moment(content.updated_at).format("YYYY-MM-DD")}
                      </td>
                      <td className={classes.td}>
                        <a href={`/app/cs-admin/content/edit/${content.id}`} className="edit-link">
                          Edit
                        </a>
                        {" | "}
                        <a href={`/app/cs-admin/content/view/${content.id}`} className="edit-link">
                          View
                        </a>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          {contents.length > 0 && (
            <Pagination
              total={total}
              page={page}
              limit={limit}
              onPageChange={(newPage) => handleFilter(newPage)}
            />
          )}
        </div>
      ) : (
        <tr>
          <td colSpan={5} style={{ padding: "16px", textAlign: "center", color: "#999" }}>
            No content found.
          </td>
        </tr>
      )}
    </div>
  );
}

CSAdminCMS.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(CSAdminCMS);
